import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { getLocalizedApiMessage } from "../../../utils/apiMessageHelper";
import {
  createCouponRequest,
  getCoupons,
  removeCoupon,
  updateCouponRequest,
} from "../../../services/api/coupons/couponsApi";

const rejectApiResponse = (responseData, fallbackMessage) => {
  if (responseData?.success === false) {
    return responseData.message ?? fallbackMessage;
  }
  return null;
};

const COUPON_CREATE_ERROR_FALLBACK = {
  en: "Failed to create coupon",
  ar: "فشل في إنشاء الكوبون",
};

const COUPON_UPDATE_ERROR_FALLBACK = {
  en: "Failed to update coupon",
  ar: "فشل في تحديث الكوبون",
};

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const toastMessages = {
  delete: {
    en: "Coupon deleted successfully!",
    ar: "تم حذف الكوبون بنجاح!",
  },
  deleteError: {
    en: "Failed to delete coupon",
    ar: "فشل في حذف الكوبون",
  },
};

export const fetchCoupons = createAsyncThunk(
  "coupons/fetchCoupons",
  async (dateRange = {}, { rejectWithValue }) => {
    try {
      const response = await getCoupons(dateRange);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch coupons");
    }
  },
);

export const deleteCoupon = createAsyncThunk(
  "coupons/deleteCoupon",
  async (couponId, { rejectWithValue }) => {
    try {
      await removeCoupon(couponId);
      return couponId;
    } catch (error) {
      return rejectWithValue("Failed to delete coupon");
    }
  },
);

export const addCoupon = createAsyncThunk(
  "coupons/addCoupon",
  async (couponData, { rejectWithValue }) => {
    try {
      const response = await createCouponRequest(couponData);

      const apiError = rejectApiResponse(
        response.data,
        COUPON_CREATE_ERROR_FALLBACK,
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        coupon: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, COUPON_CREATE_ERROR_FALLBACK),
      );
    }
  },
);

export const updateCoupon = createAsyncThunk(
  "coupons/updateCoupon",
  async (couponData, { rejectWithValue }) => {
    try {
      const response = await updateCouponRequest(couponData.id, couponData);

      const apiError = rejectApiResponse(
        response.data,
        COUPON_UPDATE_ERROR_FALLBACK,
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        coupon: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, COUPON_UPDATE_ERROR_FALLBACK),
      );
    }
  },
);

const couponsSlice = createSlice({
  name: "coupons",
  initialState: {
    couponData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        state.loading = false;
        state.couponData = action.payload;
      })
      .addCase(fetchCoupons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCoupon.fulfilled, (state, action) => {
        state.couponData = state.couponData.filter(
          (coupon) => coupon.id !== action.payload,
        );
        toast.success(toastMessages.delete[getLang()]);
      })
      .addCase(deleteCoupon.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.deleteError[getLang()]);
      })
      .addCase(addCoupon.fulfilled, (state, action) => {
        if (action.payload?.coupon) {
          state.couponData.push(action.payload.coupon);
        }
      })
      .addCase(addCoupon.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateCoupon.fulfilled, (state, action) => {
        const coupon = action.payload?.coupon;
        if (!coupon) return;

        const index = state.couponData.findIndex(
          (item) => item.id === coupon.id,
        );
        if (index !== -1) {
          state.couponData[index] = coupon;
        }
      })
      .addCase(updateCoupon.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export default couponsSlice.reducer;
